import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CaptchaActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { Customization } from '@app/shared/models/customization';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Identity } from '@app/shared/models/identity';
import { Promotion } from '@app/shared/models/promotion';
import { Transfer } from '@app/shared/models/transfer';
import { DeliveryType } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetEmailChallengeOutput } from '@smash-sdk-private/kyc/11-2023';
import { GetRecordOutput } from '@smash-sdk/domain/01-2024';
import { UploaderStatus } from '@smash-sdk/uploader';
import moment from 'moment';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, first, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TransferCommonParameters } from '../../../interfaces/transfer';
import { PrepareActions, UploadActions } from '../../../store/actions';
import * as fromUploader from '../../../store/reducers';
import { ModalSizePremiumComponent } from '../modals/modal-size-premium/modal-size-premium.component';
import { EmailFormComponent } from './email-form/email-form.component';
import { EmailValidationComponent } from './email-validation/email-validation.component';
import { LinkFormComponent } from './link-form/link-form.component';

@Component({
  selector: 'app-upload-parameters-box',
  templateUrl: './upload-parameters-box.component.html',
  styleUrls: ['./upload-parameters-box.component.scss']
})
export class UploadParametersBoxComponent implements OnInit, OnDestroy {

  @Input() uploadFormMode: DeliveryType;
  @Input() canConfigSmash: boolean;
  @Input() domainTransferSettings: TransferSettings;

  @ViewChild('emailForm', { static: false }) emailForm: EmailFormComponent;
  @ViewChild('linkForm', { static: false }) linkForm: LinkFormComponent;
  @ViewChild('emailValidation') emailValidationComponent: EmailValidationComponent;

  identity$: Observable<Identity>;

  promotions$: Observable<Promotion[]>;
  promotionsLoading$: Observable<boolean>;
  promotionSelected$: Observable<Promotion>;

  customizationEdited$: Observable<Partial<Customization>>;

  customizationToUpdate$: Observable<Customization>;
  customizationLoading$: Observable<boolean>;
  customizationError$: Observable<any>;

  transfer$: Observable<Transfer>;
  transferCommonParameters$: Observable<TransferCommonParameters>;
  transferPassword$: Observable<string>;
  transferAvailabilityDuration$: Observable<number>;

  transferQueue$: Observable<number>;
  transferQueueLoaded$: Observable<boolean>;
  transferQueueLoading$: Observable<boolean>;
 
  verifyEmailResponse$: Observable<GetEmailChallengeOutput['emailChallenge']['status']>;
  verifyEmailLoaded$: Observable<boolean>;
  verifyEmailLoading$: Observable<boolean>;
  verifyEmailError$: Observable<any>;

  validateEmailVerificationCodeResponse$: Observable<any>;
  validateEmailVerificationCodeLoaded$: Observable<boolean>;
  validateEmailVerificationCodeLoading$: Observable<boolean>;
  validateEmailVerificationCodeError$: Observable<any>;

  sendEmailVerificationLoading$: Observable<boolean>;
  sendEmailVerificationLoaded$: Observable<boolean>;
  sendEmailVerificationError$: Observable<any>;

  verificationCodeFormDisplayed$: Observable<boolean>;

  uploadStatus$: Observable<UploaderStatus>;

  drawerExpanded$: Observable<boolean>;
  transferSize$: Observable<any>;
  domain$: Observable<GetRecordOutput['record']>;
  selectedLanguage$: Observable<string>;
  countryCode$: Observable<string>;
  captchaChallenge$: Observable<string>;
  countries$: Observable<any[]>;
  transferErrorMessage$: Observable<string>;
  modalSizePremiumDisplayed$: Observable<boolean>;

  transferSize: any;
  currentDomain: any;
  domain: string;
  configFormStatus: string;
  transferStatusStarting = Constant.uploadStatus.Starting;
  tabTranslate = {
    Email: 'UPLOADER_EMAIL_TAB_LABEL',
    Link: 'UPLOADER_LINK_TAB_LABEL',
  };
  translatedAvailabilityDuration: string;
  queue: number;
  queueLoaded = false;

  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private dialog: DialogService,
    private gtmService: GoogleTagManagerService,
  ) {
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);

    this.customizationToUpdate$ = this.store$.select(fromUploader.getCustomization);
    this.customizationEdited$ = this.store$.select(fromUploader.getCustomizationEdited);
    this.customizationLoading$ = this.store$.select(fromUploader.getCustomizationLoading);
    this.customizationError$ = this.store$.select(fromUploader.getCustomizationError);

    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.transferCommonParameters$ = this.store$.select(fromUploader.getTransferCommonParameters);
    this.transferPassword$ = this.store$.select(fromUploader.getTransferPassword);
    this.transferAvailabilityDuration$ = this.store$.select(fromUploader.getTransferAvailabilityDuration);
    this.transferQueue$ = this.store$.select(fromUploader.getTransferQueue);

    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);

    this.promotions$ = this.store$.select(fromUploader.getExternalPromotions);
    this.promotionSelected$ = this.store$.select(fromUploader.getExternalPromotionSelected);
    this.promotionsLoading$ = this.store$.select(fromUploader.getExternalPromotionsLoading);

    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.drawerExpanded$ = this.store$.select(fromUploader.getConfigDrawerExpanded);
    this.transferSize$ = this.store$.select(fromUploader.getTransferSize);
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.countries$ = this.store$.select(fromRoot.getCountries);
    this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    this.captchaChallenge$ = this.store$.select(fromRoot.getCaptchaChallenge);
    this.modalSizePremiumDisplayed$ = this.store$.select(fromUploader.getModalSizePremiumDisplayed);

    this.verifyEmailResponse$ = this.store$.select(fromUploader.getVerifyEmailResponse);
    this.verifyEmailLoaded$ = this.store$.select(fromUploader.getVerifyEmailLoaded);
    this.verifyEmailLoading$ = this.store$.select(fromUploader.getVerifyEmailLoading);
    this.verifyEmailError$ = this.store$.select(fromUploader.getVerifyEmailError);

    this.validateEmailVerificationCodeResponse$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeResponse);
    this.validateEmailVerificationCodeLoaded$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoaded);
    this.validateEmailVerificationCodeLoading$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoading);
    this.validateEmailVerificationCodeError$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeError);

    this.sendEmailVerificationLoaded$ = this.store$.select(fromUploader.getSendEmailVerificationLoaded);
    this.sendEmailVerificationLoading$ = this.store$.select(fromUploader.getSendEmailVerificationLoading);
    this.sendEmailVerificationError$ = this.store$.select(fromUploader.getSendEmailVerificationError);

    this.verificationCodeFormDisplayed$ = this.store$.select(fromUploader.getVerificationCodeFormDisplayed);

    this.subscriptions.push(
      this.store$.select(fromUploader.getConfigFormStatus).subscribe(status => { this.configFormStatus = status; }),
      this.domain$.subscribe(domain => { this.currentDomain = domain; }),
      combineLatest([this.transferAvailabilityDuration$, this.selectedLanguage$]).subscribe(([availabilityDuration, language]) => {
        moment.locale(language);
        this.translatedAvailabilityDuration = moment().add(availabilityDuration, 'seconds').format('L');
      }),

      this.modalSizePremiumDisplayed$.pipe(distinctUntilChanged()).subscribe((displayed) => {
        if (displayed === true) {
          this.openModalSizePremium();
        }
      }),

      this.verifyEmailResponse$.subscribe(response => {
        if (response === 'Clean') {
          this.startUpload();
        } else if (response === 'Dirty') {
          this.store$.dispatch(PrepareActions.DisplayVerificationCodeForm());
        }
      }),
      this.validateEmailVerificationCodeResponse$.subscribe(response => {
        if (response === 'Clean') {
          this.startUpload();
          this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
        }
      }),
      // this.captchaChallenge$.subscribe(challenge => { // For now don't stop the user
      //   if (challenge === 'Success' ) { 
      //     this.store$.dispatch(CaptchaActions.resetVerifyCaptcha());
      //     this.startUpload();
      //   }
      // })
    );
  }

  ngOnInit() {
    // TODO Remove this workaround and check timing initialization synchronization localstorage <-> store redux
    if (!this.uploadFormMode || this.domainTransferSettings?.delivery?.options?.indexOf(this.uploadFormMode) === -1) {
      setTimeout(() => this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode: this.domainTransferSettings.delivery.default as DeliveryType })));
    }
  }

  onSelectSmashMode(uploadFormMode: DeliveryType): void {
    this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
  }

  get emailInput() {
    return this.uploadFormMode === 'Email' ? this.emailForm.emailForm.value.sender.email : this.linkForm.linkForm.value.sender.email
  }

  async handleFormSubmitted() {
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    const verifyEmailResponse = await this.verifyEmailResponse$.pipe(take(1)).toPromise();
    if (verifyEmailResponse === 'Clean' || (identity?.username && identity?.username === this.emailInput)) {
      this.startUpload(); // for now start upload no matter what
      this.store$.dispatch(CaptchaActions.verifyCaptcha({ action: 'smashUpload' }));
    } else {
      this.store$.dispatch(PrepareActions.VerifyEmail({ username: this.emailInput }));
    }
  }

  async startUpload() {
    const transferSize = await this.transferSize$.pipe(first()).toPromise();
    this.store$.dispatch(UploadActions.Upload());
    this.gtmService.pushTag({
      event: Constant.eventGTM.START_TRANSFER,
      transfer_size: transferSize,
    });
  }

  async openModalSizePremium() {
    const countries = await this.countries$.pipe(first(countries => !!countries)).toPromise();
    const countryCode = await this.countryCode$.pipe(first(countryCode => !!countryCode)).toPromise();
    const queue = await this.transferQueue$.pipe(first(queue => !!queue)).toPromise();
    const transferSize = await this.transferSize$.pipe(first(size => !!size)).toPromise();
    this.dialog.open(ModalSizePremiumComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'sm',
        data: {
          countries,
          countryCode,
          queue,
          transferSize,
        },
      }
    );
  }

  submitVerificationCode() {
    this.emailValidationComponent.submit();
  }

  submitForm() {
    if (this.uploadFormMode) {
      switch (this.uploadFormMode) {
        case 'Email':
          this.emailForm.onSubmit();
          break;
        case 'Link':
          this.linkForm.onSubmit();
          break;
        default:
          break;
      }
    }
  }

  handleOpenCloseDrawer(): void {
    if (this.canConfigSmash && this.configFormStatus !== 'INVALID') {
      this.store$.dispatch(PrepareActions.toggleConfigDrawerExpanded());
    }
  }

  handleCloseVerificationCode(): void {
    this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
    this.store$.dispatch(PrepareActions.ResetVerifyEmail());
    this.emailValidationComponent.verificationCodeComponent.reset();
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
